import { useEffect, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { useI18n } from "../../lib/i18n";

/**
 * Handing a conversation to a teammate, with a line of context for them.
 *
 * The note is optional because most hand-overs are obvious from the thread;
 * when it is not, "wants a refund, order 123" saves the next person reading
 * forty messages. It lands in the thread as an internal note and in their
 * notification.
 */
export function HandoverDialog({
  name,
  onCancel,
  onConfirm,
}: {
  name: string;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}): JSX.Element {
  const { t: all } = useI18n();
  const t = all.conv;
  const [note, setNote] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    field.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div class="dialog-scrim" onClick={onCancel}>
      <form
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="handover-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm(note.trim());
        }}
      >
        <h2 id="handover-title">{t.handoverTitle(name)}</h2>
        <label>
          {t.handoverNote} <span class="hint">{t.handoverHint}</span>
          <textarea
            ref={field}
            class="plain"
            rows={3}
            maxLength={1000}
            placeholder={t.handoverPlaceholder}
            value={note}
            onInput={(e) => setNote(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onConfirm(note.trim());
              }
            }}
          />
        </label>
        <div class="dialog-actions">
          <button type="button" class="btn" onClick={onCancel}>
            {all.common.cancel}
          </button>
          <button type="submit" class="btn primary">
            {t.handoverConfirm}
          </button>
        </div>
      </form>
    </div>
  );
}
